import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import '../ConfirmDialog/ConfirmDialog.css';
import './SkillNameConflictDialog.css';

// Popup do conflito de nome em "Adicionar Skill". Nomes de skill sao unicos
// na plataforma inteira (constraint em `skills.name`), entao subir um SKILL.md
// com um nome ja ocupado precisa de uma decisao do usuario - antes isso era
// ou uma substituicao confirmada so' quando a skill era dele e ja estava no
// cache da aba "minhas" (ConfirmDialog de 2 botoes), ou um erro seco "nome ja
// esta em uso" sem saida nenhuma quando o nome era de outra pessoa.
//
// As duas saidas agora convivem no mesmo popup:
//   - "Substituir": so aparece quando a skill que ocupa o nome e' do PROPRIO
//     usuario (`conflict.owned_by_user`, vindo do 409 do backend) - publica
//     uma nova versao dela, comportamento historico de create_skill.
//   - campo de nome + "Criar com este nome": sempre disponivel, cria uma
//     skill NOVA (o backend reescreve o frontmatter do SKILL.md junto, ver
//     helpers.renomear_skill_md).
//
// O sufixo "-2" ja vem preenchido so' como ponto de partida editavel: o nome
// que o usuario escolher pode estar ocupado tambem, e nesse caso o popup
// continua aberto com `errorMessage` em vez de fechar e virar um alerta.
export default function SkillNameConflictDialog({
    isOpen,
    conflict,
    maxNameChars,
    busy = false,
    errorMessage = '',
    onReplace,
    onCreateWithName,
    onCancel,
}) {
    const { t } = useTranslation('chat');
    const [name, setName] = useState('');
    const inputRef = useRef(null);
    const wasOpen = useRef(false);

    const conflictingName = conflict?.name || '';

    useEffect(() => {
        // Reinicia a sugestao so na ABERTURA: um segundo conflito (o nome
        // escolhido tambem estava em uso) mantem o que o usuario digitou,
        // pra ele so ajustar em vez de recomecar.
        if (isOpen && !wasOpen.current) {
            const sugestao = `${conflictingName}-2`.slice(0, maxNameChars);
            setName(sugestao);
            window.requestAnimationFrame(() => inputRef.current?.focus());
        }
        wasOpen.current = isOpen;
    }, [isOpen, conflictingName, maxNameChars]);

    // Mesmo guard do clique no overlay (linha abaixo): não cancela enquanto uma requisição
    // está em andamento (`busy`).
    useEscapeToClose(onCancel, isOpen && !busy);

    if (!isOpen) return null;

    const trimmed = name.trim();
    const tooLong = trimmed.length > maxNameChars;
    const podeCriar = trimmed.length > 0 && !tooLong && trimmed !== conflictingName && !busy;

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (podeCriar) onCreateWithName(trimmed);
    };

    return (
        <div
            className="modal-overlay-confirm-dialog"
            onClick={handleOverlayClick}
            data-testid="skill-name-conflict-overlay"
        >
            <form
                className="modal-content-confirm-dialog skill-name-conflict-dialog"
                onSubmit={handleSubmit}
                data-testid="skill-name-conflict-dialog"
            >
                <div className="confirm-dialog-header">
                    <p className="confirm-dialog-title">{t('skills_manager_name_conflict_title')}</p>
                    <img src="/imagens/x.svg" onClick={onCancel} className="confirm-dialog-cancel-img" alt="" />
                </div>
                <p className="confirm-dialog-message">
                    {conflict?.owned_by_user
                        ? t('skills_manager_name_conflict_message_own', { name: conflictingName })
                        : t('skills_manager_name_conflict_message_other', { name: conflictingName })}
                </p>

                <label className="skill-name-conflict-field">
                    <span className="skill-name-conflict-label">
                        {t('skills_manager_name_conflict_field_label')}
                    </span>
                    <input
                        ref={inputRef}
                        type="text"
                        className="skill-name-conflict-input"
                        value={name}
                        maxLength={maxNameChars}
                        disabled={busy}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="skill-name-conflict-input"
                    />
                </label>

                {errorMessage ? (
                    <p className="skill-name-conflict-error" data-testid="skill-name-conflict-error">
                        {errorMessage}
                    </p>
                ) : null}

                <div className="confirm-dialog-actions">
                    {/* <button
                        type="button"
                        className="confirm-dialog-cancel-button"
                        onClick={onCancel}
                        disabled={busy}
                        data-testid="skill-name-conflict-cancel"
                    >
                        {t('skills_replace_cancel_button')}
                    </button> */}
                    {conflict?.owned_by_user ? (
                        <button
                            type="button"
                            className="skill-name-conflict-replace-button"
                            onClick={onReplace}
                            disabled={busy}
                            data-testid="skill-name-conflict-replace"
                        >
                            {t('skills_replace_confirm_button')}
                        </button>
                    ) : null}
                    <button
                        type="submit"
                        className="confirm-dialog-confirm-button"
                        disabled={!podeCriar}
                        data-testid="skill-name-conflict-create"
                    >
                        {t('skills_manager_name_conflict_create_button')}
                    </button>
                </div>
            </form>
        </div>
    );
}
